import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { METHOD_LABEL, RECOVERY_LABEL, todayISO } from '@/lib/hr'
import type { HREmployee, HRPayoutMethod, HRRecoveryPlan } from '@/types/hr'
import { Field, NativeSelect, errMsg } from './shared'

// Money in and out of a person's account (F-HR-N4 / N6). Both post an
// expense under Salaries; neither touches the till.

export function invalidateHRMoney(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ['overview', 'employees', 'employee', 'statement', 'advances', 'payouts', 'runs', 'run']) {
    qc.invalidateQueries({ queryKey: ['hr', k] })
  }
  qc.invalidateQueries({ queryKey: ['expenses'] })
}

function MethodFields({
  method,
  setMethod,
  reference,
  setReference,
}: {
  method: HRPayoutMethod
  setMethod: (m: HRPayoutMethod) => void
  reference: string
  setReference: (s: string) => void
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Paid by">
        <NativeSelect value={method} onChange={(e) => setMethod(e.target.value as HRPayoutMethod)}>
          {(Object.keys(METHOD_LABEL) as HRPayoutMethod[]).map((m) => (
            <option key={m} value={m}>
              {METHOD_LABEL[m]}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <Field label="Reference" hint={method === 'cash' ? 'Optional — e.g. who handed it over' : 'Transaction or slip number'}>
        <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={80} />
      </Field>
    </div>
  )
}

export function PayDialog({
  open,
  onClose,
  person,
  runId,
  maxAmount,
  title,
}: {
  open: boolean
  onClose: () => void
  person: Pick<HREmployee, 'id' | 'full_name' | 'payout_method' | 'bank_name' | 'account_number'> | null
  runId?: string
  maxAmount: number
  title?: string
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [amount, setAmount] = useState('')
  const [paidOn, setPaidOn] = useState(todayISO())
  const [method, setMethod] = useState<HRPayoutMethod>('cash')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  useEffect(() => {
    if (open) {
      setAmount(maxAmount > 0 ? String(Math.round(maxAmount * 100) / 100) : '')
      setPaidOn(todayISO())
      setMethod(person?.payout_method ?? 'cash')
      setReference('')
      setNote('')
    }
  }, [open, maxAmount, person])
  const pay = useMutation({
    mutationFn: () =>
      apiClient.hrPay({ employee_id: person!.id, run_id: runId, amount: Number(amount), paid_on: paidOn, method, reference, note }),
    onSuccess: () => {
      toastHelpers.success('Payment recorded', `${person?.full_name}: ${formatCurrency(Number(amount))} — added to Expenses → Salaries`)
      invalidateHRMoney(qc)
      onClose()
    },
    onError: (err) => toastHelpers.error('Couldn’t record the payment', errMsg(err)),
  })
  const n = Number(amount)
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title ?? `Pay ${person?.full_name ?? ''}`}</DialogTitle>
          <DialogDescription>
            {formatCurrency(Math.max(0, maxAmount))} is owed. Record money paid from the office or the owner — not from the till.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Amount">
            <Input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Paid on">
            <Input type="date" value={paidOn} max={todayISO()} onChange={(e) => setPaidOn(e.target.value)} />
          </Field>
        </div>
        <MethodFields method={method} setMethod={setMethod} reference={reference} setReference={setReference} />
        {method === 'bank' && person?.account_number && (
          <p className="text-xs text-muted-foreground">
            Account on file: {person.bank_name} {person.account_number}
          </p>
        )}
        <Field label="Note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => pay.mutate()} disabled={!person || !(n > 0) || n > maxAmount + 0.005 || pay.isPending}>
            Record payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function AdvanceDialog({
  open,
  onClose,
  people,
  personId,
}: {
  open: boolean
  onClose: () => void
  people: Pick<HREmployee, 'id' | 'full_name' | 'payout_method'>[]
  personId?: string
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [emp, setEmp] = useState('')
  const [amount, setAmount] = useState('')
  const [givenOn, setGivenOn] = useState(todayISO())
  const [reason, setReason] = useState('')
  const [plan, setPlan] = useState<HRRecoveryPlan>('next_payroll')
  const [installments, setInstallments] = useState('3')
  const [monthly, setMonthly] = useState('')
  const [method, setMethod] = useState<HRPayoutMethod>('cash')
  const [reference, setReference] = useState('')
  useEffect(() => {
    if (open) {
      setEmp(personId ?? '')
      setAmount('')
      setGivenOn(todayISO())
      setReason('')
      setPlan('next_payroll')
      setInstallments('3')
      setMonthly('')
      setReference('')
      setMethod(people.find((p) => p.id === personId)?.payout_method ?? 'cash')
    }
  }, [open, personId, people])
  const give = useMutation({
    mutationFn: () =>
      apiClient.hrGiveAdvance({
        employee_id: emp,
        amount: Number(amount),
        given_on: givenOn,
        reason,
        recovery_plan: plan,
        installments: plan === 'installments' ? Number(installments) : undefined,
        monthly_amount: plan === 'fixed_monthly' ? Number(monthly) : undefined,
        method,
        reference,
      }),
    onSuccess: () => {
      toastHelpers.success('Advance recorded', 'Added to Expenses → Salaries; payroll will recover it')
      invalidateHRMoney(qc)
      onClose()
    },
    onError: (err) => toastHelpers.error('Couldn’t record the advance', errMsg(err)),
  })
  const a = Number(amount)
  const per = plan === 'installments' ? Math.ceil(a / Math.max(1, Number(installments))) : plan === 'fixed_monthly' ? Number(monthly) : a
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Give an advance</DialogTitle>
          <DialogDescription>Money given ahead of salary. Payroll takes it back the way you choose, never more than the month’s pay.</DialogDescription>
        </DialogHeader>
        <Field label="Person">
          <NativeSelect value={emp} onChange={(e) => setEmp(e.target.value)} disabled={!!personId}>
            <option value="">Choose…</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Amount">
            <Input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Given on">
            <Input type="date" value={givenOn} max={todayISO()} onChange={(e) => setGivenOn(e.target.value)} />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Paid back">
            <NativeSelect value={plan} onChange={(e) => setPlan(e.target.value as HRRecoveryPlan)}>
              {(Object.keys(RECOVERY_LABEL) as HRRecoveryPlan[]).map((p) => (
                <option key={p} value={p}>
                  {RECOVERY_LABEL[p]}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {plan === 'installments' && (
            <Field label="Over how many months">
              <Input type="number" min={2} max={36} value={installments} onChange={(e) => setInstallments(e.target.value)} />
            </Field>
          )}
          {plan === 'fixed_monthly' && (
            <Field label="Each month">
              <Input type="number" min={0} value={monthly} onChange={(e) => setMonthly(e.target.value)} />
            </Field>
          )}
        </div>
        {a > 0 && per > 0 && <p className="text-xs text-muted-foreground">Payroll will take back {formatCurrency(per)} a month until it’s cleared.</p>}
        <MethodFields method={method} setMethod={setMethod} reference={reference} setReference={setReference} />
        <Field label="Reason">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => give.mutate()} disabled={!emp || !(a > 0) || give.isPending || (plan === 'fixed_monthly' && !(Number(monthly) > 0))}>
            Record advance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** One reason-required confirmation (write-off, reverse, reopen, send back). */
export function ReasonDialog({
  open,
  onClose,
  title,
  description,
  action,
  destructive,
  busy,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  title: string
  description: string
  action: string
  destructive?: boolean
  busy?: boolean
  onConfirm: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  useEffect(() => {
    if (open) setReason('')
  }, [open])
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={destructive ? 'destructive' : 'default'} disabled={!reason.trim() || busy} onClick={() => onConfirm(reason.trim())}>
            {action}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
